import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Mail, Users, Settings, Plus, Upload, Search, Download, RefreshCw, MessageSquare, FileText, Edit2, Save, X, Gift } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import EmailCampaigns from './commsmarketing/EmailCampaigns';
import SmsCampaigns from './commsmarketing/SmsCampaigns';
import BirthdayMessages from './commsmarketing/BirthdayMessages';
import AuditTrailViewer from './commsmarketing/AuditTrailViewer';
import POSHeader from './kiosk/POSSection/POSHeader';

const CommsMarketing = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const isKioskMode = location.pathname.includes('/kiosk/');

  // Helper to get correct auth token
  const getAuthToken = () => {
  return localStorage.getItem('kioskAuthToken') || 
         localStorage.getItem('adminAuthToken') || 
         localStorage.getItem('authToken');
};

  const [activeTab, setActiveTab] = useState('contacts');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);

  // Contacts Tab States
  const [contacts, setContacts] = useState([]);
  const [contactStats, setContactStats] = useState({ 
    totalContacts: 0, 
    subscribed: 0, 
    unsubscribed: 0, 
    newThisMonth: 0, 
    phoneContacts: 0 
  });
  const [showAddContact, setShowAddContact] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [contactFilters, setContactFilters] = useState({
    search: '',
    tags: '',
    source: '',
  });
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    businessUnits: [],
    communicationMethod: 'Both',
    tags: '',
  });
  
  // Edit Contact State
  const [editingContact, setEditingContact] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    businessUnits: [],
    communicationMethod: 'Both',
    tags: [],
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Settings Tab States
  const [settings, setSettings] = useState({
    senderName: "Ella's Place Salon, Spa & Ladies Gym",
    senderEmail: 'info@ellasplace.net',
    replyToEmail: 'info@ellasplace.net',
    companyAddress: '',
    logo: null,
    emailFooter: "Sent with love from Ella's Place Salon, Spa & Ladies Gym",
    trackOpens: true,
    trackClicks: true,
    doubleOptIn: false,
    unsubscribeCompliance: true,
    smtp: {
      host: 'ellasplace.net',
      port: '465',
      secure: true,
      username: 'info@ellasplace.net',
      password: '',
    },
    sms: {
      provider: 'none',
      apiKey: '',
      apiSecret: '',
      senderId: "Ella's Place",
      enabled: false
    }
  });

  const fileInputRef = useRef(null);

  // Get unique tags from all contacts
  const getUniqueTags = () => {
    const tagsSet = new Set();
    contacts.forEach(contact => {
      if (contact.tags && Array.isArray(contact.tags)) {
        contact.tags.forEach(tag => tagsSet.add(tag));
      }
    });
    return Array.from(tagsSet).sort();
  };

  // Get unique sources from all contacts
  const getUniqueSources = () => {
    const sourcesSet = new Set();
    contacts.forEach(contact => {
      if (contact.source) {
        sourcesSet.add(contact.source);
      }
    });
    return Array.from(sourcesSet).sort();
  };

  // Format display name
  const getDisplayName = () => {
    if (user?.firstName) return user.firstName;
    if (user?.username) {
      const firstName = user.username.includes('.') 
        ? user.username.split('.')[0] 
        : user.username;
      return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    }
    return 'User';
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    if (isKioskMode) {
      navigate('/kiosk/login');
    } else {
      navigate('/login');
    }
  };

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/comms-marketing/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const contactsData = response.data.contacts || [];
      setContacts(contactsData);
      
      // Calculate phoneContacts stat from contacts array
      const phoneCount = contactsData.filter(c => c.phone && c.phone.trim() !== '').length;
      
      setContactStats({
        ...response.data.stats,
        phoneContacts: phoneCount
      });
      setError('');
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Failed to load contacts');
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, []);

  // Add contact
  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      const token = getAuthToken();
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/comms-marketing/contacts`,
        {
          ...newContact,
          tags: newContact.tags.split(',').map(t => t.trim()).filter(Boolean),
          businessUnits: newContact.businessUnits
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Contact added successfully');
      setShowAddContact(false);
      setNewContact({ name: '', email: '', phone: '', dateOfBirth: '', businessUnits: [], communicationMethod: 'Both', tags: '' });
      fetchContacts();
    } catch (err) {
      console.error('Error adding contact:', err);
      toast.error(err.response?.data?.error || 'Failed to add contact');
    }
  };

  // Start editing a contact
  const handleStartEdit = (contact) => {
    setEditingContact(contact._id);
    setEditForm({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      dateOfBirth: contact.dateOfBirth || '',
      businessUnits: contact.businessUnits || [],
      communicationMethod: contact.communicationMethod || 'Both',
      tags: contact.tags || [],
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingContact(null);
    setEditForm({
      name: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      businessUnits: [],
      communicationMethod: 'Both',
      tags: [],
    });
  };

  // Save edited contact
  const handleSaveEdit = async (contactId) => {
    try {
      const token = getAuthToken();
      await axios.put(
        `${import.meta.env.VITE_API_URL}/api/comms-marketing/contacts/${contactId}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Contact updated successfully');
      setEditingContact(null);
      fetchContacts();
    } catch (err) {
      console.error('Error updating contact:', err);
      toast.error(err.response?.data?.error || 'Failed to update contact');
    }
  };

  // Import contacts
  const handleImportContacts = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = getAuthToken();
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/comms-marketing/contacts/import`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      toast.success(response.data.message);
      setShowImportModal(false);
      fetchContacts();
    } catch (err) {
      console.error('Error importing contacts:', err);
      toast.error(err.response?.data?.error || 'Failed to import contacts');
    }
  };

  // Sync customers
  const handleSyncCustomers = async () => {
    try {
      setSyncLoading(true);
      const token = getAuthToken();
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/comms-marketing/contacts/sync-from-customers`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      fetchContacts();
    } catch (err) {
      console.error('Error syncing customers:', err);
      toast.error(err.response?.data?.error || 'Failed to sync customers');
    } finally {
      setSyncLoading(false);
    }
  };

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/comms-marketing/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'contacts') {
      fetchContacts();
    } else if (activeTab === 'settings') {
      fetchSettings();
    }
  }, [activeTab, fetchContacts, fetchSettings]);

  // Filter contacts - UPDATED: Minimum 3 characters for search
  const filteredContacts = contacts.filter(contact => {
    const searchTerm = contactFilters.search.toLowerCase();
    
    // UPDATED: Only apply search if 3+ characters
    const matchesSearch = contactFilters.search.length < 3 || 
      contact.name.toLowerCase().includes(searchTerm) ||
      (contact.email && contact.email.toLowerCase().includes(searchTerm)) ||
      (contact.phone && contact.phone.includes(contactFilters.search));
    
    // FIXED: Use exact match for tags instead of includes to avoid "active" matching "inactive"
    const matchesTags = !contactFilters.tags || 
      (contact.tags && contact.tags.length > 0 && contact.tags.some(tag => 
        tag.toLowerCase() === contactFilters.tags.toLowerCase()
      ));
    
    const matchesSource = !contactFilters.source || contact.source === contactFilters.source;
    
    return matchesSearch && matchesTags && matchesSource;
  });

  const paginatedContacts = filteredContacts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalContactPages = Math.ceil(filteredContacts.length / itemsPerPage);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Format DOB for display (MM-DD format to "Month Day")
  const formatDOB = (dob) => {
    if (!dob) return 'N/A';
    
    // dob is in MM-DD format (e.g., "05-19")
    const parts = dob.split('-');
    if (parts.length !== 2) return dob;
    
    const month = parseInt(parts[0]);
    const day = parseInt(parts[1]);
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    if (month < 1 || month > 12) return dob;
    
    return `${monthNames[month - 1]} ${day}`;
  };

  // Contacts Tab
  const renderContactsTab = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Total Contacts</h3>
          <p className="text-2xl font-bold text-yellow-600">{contactStats.totalContacts}</p>
          <p className="text-xs text-gray-600 mt-2">All contacts</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Email Subscribers</h3>
          <p className="text-2xl font-bold text-green-600">{contactStats.subscribed}</p>
          <p className="text-xs text-gray-600 mt-2">Active subscribers</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Phone Contacts</h3>
          <p className="text-2xl font-bold text-blue-600">{contactStats.phoneContacts}</p>
          <p className="text-xs text-gray-600 mt-2">Available for SMS</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">New This Month</h3>
          <p className="text-2xl font-bold text-purple-600">{contactStats.newThisMonth}</p>
          <p className="text-xs text-gray-600 mt-2">Recent additions</p>
        </div>
      </div>

      {/* Action Bar */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => setShowAddContact(true)}
              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm flex items-center gap-2"
            >
              <Plus size={16} />
              Add Contact
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center gap-2"
            >
              <Upload size={16} />
              Import Contacts
            </button>
            <button
              onClick={handleSyncCustomers}
              disabled={syncLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw size={16} className={syncLoading ? 'animate-spin' : ''} />
              {syncLoading ? 'Syncing...' : 'Sync Customers'}
            </button>
          </div>
          <button
            onClick={() => {
              const ws = XLSX.utils.json_to_sheet(contacts);
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, 'Contacts');
              XLSX.writeFile(wb, 'contacts.xlsx');
            }}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm flex items-center gap-2"
          >
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Filters - UPDATED: Dynamic tags and sources */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
            <input
              type="text"
              placeholder="Search contacts (min 3 chars)..."
              value={contactFilters.search}
              onChange={(e) => setContactFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
            />
            {contactFilters.search.length > 0 && contactFilters.search.length < 3 && (
              <p className="text-xs text-gray-500 mt-1">Type at least 3 characters to search</p>
            )}
          </div>
          <select
            value={contactFilters.tags}
            onChange={(e) => setContactFilters(prev => ({ ...prev, tags: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
          >
            <option value="">All Tags</option>
            {getUniqueTags().map(tag => (
              <option key={tag} value={tag}>{tag}</option>
            ))}
          </select>
          <select
            value={contactFilters.source}
            onChange={(e) => setContactFilters(prev => ({ ...prev, source: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
          >
            <option value="">All Sources</option>
            {getUniqueSources().map(source => (
              <option key={source} value={source}>{source}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Contacts List - UPDATED: New columns */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Contact Directory</h3>
          <p className="text-sm text-gray-600 mt-1">
            Showing {paginatedContacts.length} of {filteredContacts.length} contacts (Total: {contacts.length})
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  DOB
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Communication
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tags
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Added
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedContacts.map((contact) => (
                <tr key={contact._id} className="hover:bg-gray-50">
                  {editingContact === contact._id ? (
                    <>
                      {/* Edit Mode */}
                      <td className="px-4 py-4">
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded mb-1"
                          placeholder="Name"
                        />
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded mb-1"
                          placeholder="Email"
                        />
                        <input
                          type="text"
                          value={editForm.phone}
                          onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="Phone"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <input
                          type="text"
                          value={editForm.dateOfBirth}
                          onChange={(e) => setEditForm(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="MM-DD"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <select
                          value={editForm.communicationMethod}
                          onChange={(e) => setEditForm(prev => ({ ...prev, communicationMethod: e.target.value }))}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        >
                          <option value="Both">Both</option>
                          <option value="Email">Email</option>
                          <option value="SMS">SMS</option>
                          <option value="None">None</option>
                        </select>
                      </td>
                      <td className="px-4 py-4">
                        <input
                          type="text"
                          value={editForm.tags.join(', ')}
                          onChange={(e) => setEditForm(prev => ({ ...prev, tags: e.target.value.split(',').map(t => t.trim()) }))}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="Tags (comma separated)"
                        />
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {formatDate(contact.createdAt)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveEdit(contact._id)}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                            title="Save"
                          >
                            <Save size={16} />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                            title="Cancel"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      {/* View Mode */}
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                        <div className="text-xs text-gray-500">{contact.email || 'No email'}</div>
                        <div className="text-xs text-gray-500">{contact.phone || 'No phone'}</div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {formatDOB(contact.dateOfBirth)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-gray-700">
                            {contact.communicationMethod || 'Both'}
                          </span>
                          <div className="flex gap-1">
                            {(contact.communicationMethod === 'Both' || contact.communicationMethod === 'Email') && contact.email && (
                              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">Email</span>
                            )}
                            {(contact.communicationMethod === 'Both' || contact.communicationMethod === 'SMS') && contact.phone && (
                              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">SMS</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1">
                          {contact.tags && contact.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                          {(!contact.tags || contact.tags.length === 0) && (
                            <span className="text-xs text-gray-400">No tags</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {formatDate(contact.createdAt)}
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => handleStartEdit(contact)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalContactPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalContactPages))}
                disabled={currentPage === totalContactPages}
                className="ml-2 relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-xs text-gray-700">
                  Page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalContactPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-1 rounded-l-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  {totalContactPages <= 7 ? (
                    // Show all pages if 7 or fewer
                    [...Array(totalContactPages)].map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`relative inline-flex items-center px-3 py-1 border text-xs font-medium ${
                          currentPage === i + 1
                            ? 'z-10 bg-yellow-50 border-yellow-500 text-yellow-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))
                  ) : (
                    // Show smart pagination for many pages
                    <>
                      {currentPage > 3 && (
                        <>
                          <button
                            onClick={() => setCurrentPage(1)}
                            className="relative inline-flex items-center px-3 py-1 border bg-white border-gray-300 text-gray-500 hover:bg-gray-50 text-xs font-medium"
                          >
                            1
                          </button>
                          {currentPage > 4 && (
                            <span className="relative inline-flex items-center px-3 py-1 border border-gray-300 bg-white text-xs text-gray-700">
                              ...
                            </span>
                          )}
                        </>
                      )}
                      {[...Array(totalContactPages)].map((_, i) => {
                        const pageNum = i + 1;
                        if (
                          pageNum === currentPage ||
                          pageNum === currentPage - 1 ||
                          pageNum === currentPage + 1 ||
                          (currentPage <= 2 && pageNum <= 3) ||
                          (currentPage >= totalContactPages - 1 && pageNum >= totalContactPages - 2)
                        ) {
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`relative inline-flex items-center px-3 py-1 border text-xs font-medium ${
                                currentPage === pageNum
                                  ? 'z-10 bg-yellow-50 border-yellow-500 text-yellow-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        }
                        return null;
                      })}
                      {currentPage < totalContactPages - 2 && (
                        <>
                          {currentPage < totalContactPages - 3 && (
                            <span className="relative inline-flex items-center px-3 py-1 border border-gray-300 bg-white text-xs text-gray-700">
                              ...
                            </span>
                          )}
                          <button
                            onClick={() => setCurrentPage(totalContactPages)}
                            className="relative inline-flex items-center px-3 py-1 border bg-white border-gray-300 text-gray-500 hover:bg-gray-50 text-xs font-medium"
                          >
                            {totalContactPages}
                          </button>
                        </>
                      )}
                    </>
                  )}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalContactPages))}
                    disabled={currentPage === totalContactPages}
                    className="relative inline-flex items-center px-2 py-1 rounded-r-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      {isKioskMode ? (
        <POSHeader user={user} onLogout={handleLogout} />
      ) : (
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Communications & Marketing</h1>
                <p className="text-sm text-gray-600 mt-1">Manage contacts, campaigns, and settings</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">Welcome, {getDisplayName()}</span>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <nav className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('contacts')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'contacts'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users size={16} />
              Contacts
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'email'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Mail size={16} />
              Email Campaigns
            </button>
            <button
              onClick={() => setActiveTab('sms')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'sms'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <MessageSquare size={16} />
              SMS Campaigns
            </button>
            <button
              onClick={() => setActiveTab('birthday')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'birthday'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Gift size={16} />
              Birthday Messages
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'audit'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText size={16} />
              Audit Trail
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'settings'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings size={16} />
              Settings
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {activeTab === 'contacts' && renderContactsTab()}
            {activeTab === 'email' && <EmailCampaigns contacts={contacts} />}
            {activeTab === 'sms' && <SmsCampaigns contacts={contacts} />}
            {activeTab === 'birthday' && <BirthdayMessages />}
            {activeTab === 'audit' && <AuditTrailViewer />}
            {activeTab === 'settings' && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4">Settings</h2>
                <p className="text-gray-600">Settings management coming soon...</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Contact Modal */}
      {showAddContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Add New Contact</h3>
            <form onSubmit={handleAddContact} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  required
                  value={newContact.name}
                  onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="text"
                  value={newContact.phone}
                  onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth (MM-DD)</label>
                <input
                  type="text"
                  placeholder="01-15"
                  value={newContact.dateOfBirth}
                  onChange={(e) => setNewContact(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Communication Method</label>
                <select
                  value={newContact.communicationMethod}
                  onChange={(e) => setNewContact(prev => ({ ...prev, communicationMethod: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="Both">Both (Email & SMS)</option>
                  <option value="Email">Email Only</option>
                  <option value="SMS">SMS Only</option>
                  <option value="None">None</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={newContact.tags}
                  onChange={(e) => setNewContact(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="VIP, Salon, Regular"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium"
                >
                  Add Contact
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddContact(false);
                    setNewContact({ name: '', email: '', phone: '', dateOfBirth: '', businessUnits: [], communicationMethod: 'Both', tags: '' });
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Import Contacts</h3>
            <p className="text-sm text-gray-600 mb-4">
              Upload an Excel file (.xlsx) with columns: name, email, phone, tags
            </p>
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls"
              onChange={handleImportContacts}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100"
            />
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommsMarketing;